/**
 * The 1px-gutter grid, and all four breakpoints of components.md §1.
 *
 * ```
 * grid-template-columns: minmax(0,1fr) var(--ud-orderbook-width) var(--ud-ticket-width)
 * gap: var(--ud-panel-gap)   on   background: var(--ud-color-bg-canvas)
 * ```
 *
 * | Range | Behaviour |
 * |---|---|
 * | ≥1440px | Full three-column as drawn |
 * | 1120–1439 | Orderbook narrows to 260px; "next markets" folds into the bottom tabs |
 * | 768–1119 | Ticket becomes a bottom sheet; orderbook and trades become tabs beside the chart |
 * | <768px | Single column: strip → chart → sticky UP/DOWN → sheet |
 *
 * **Pro mode collapses the two right-hand regions into one.** The pro ladder is
 * a single control that replaces both the orderbook and the ticket, so it wants
 * their combined width rather than either — `wide` is that region, and where it
 * is passed `book` and `ticket` are not rendered at all. It exists only from
 * 1120px up, because below that there is no ticket column to absorb and
 * `<TradeRoute>` does not pass it; the four rows above are unchanged.
 *
 * **The columns are a CSS concern and the sheet is a React one.** The first two
 * breakpoints only change track sizes, so they are media queries and cost
 * nothing. The bottom two remove a column from the DOM, and a ticket rendered
 * off-screen at 767px is a live order form the user cannot see — so the caller
 * asks `useBreakpoint()` and renders the ticket into a `<Sheet>` instead. This
 * component owns the tracks; `AppShell` owns that decision.
 *
 * The orderbook's 260px at the middle breakpoint is a local override rather than
 * a token, because `--ud-orderbook-width` is the design system's one value for
 * that panel and a second token named `orderbook-width-narrow` would invite a
 * third.
 *
 * **The ticket column runs the full height of the page, and the tables stop at
 * its edge.** Until this it did not: the bottom tabs were a sibling of the whole
 * grid in `<AppShell>`, so they spanned every column and the ticket ended 240px
 * short of the bottom — cut off mid-form, with the payout rows and the primary
 * button behind a scroll on any viewport under about 1100px tall. The order
 * ticket is the one column whose content has no upper bound (the payout block
 * grows with the quote, the error region with whatever the venue said), so it is
 * the column that should get the page's whole height rather than the one that
 * gives it up.
 *
 * **The ladder runs the full height too, on the same argument.** Its content is
 * a fixed 24 rows plus a header and the mid row — about 576px — so a column cut
 * short by the tables clipped the far side of the book, which is the half a
 * taker reads. Both it and the ticket are columns whose content does not shrink
 * to fit; the chart is the one panel that is happy at any height, so the chart
 * is the one that gives.
 *
 * That makes the layout three full-height regions side by side rather than one
 * grid, and **the tables sit under the chart alone**. `aside` and `bottom`
 * therefore live here rather than in `<AppShell>` — the thing that decides where
 * a column ends is the thing that has to decide what sits under it — and `aside`
 * stays **above** `bottom`, which is the ordering `components.md` §2.8 requires:
 * the resolving market may take space from the tables and never from the
 * tradable market.
 */

import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'

export interface PanelGridProps {
  chart: ReactNode
  /** Omitted in pro mode, where `wide` replaces it. */
  book?: ReactNode
  /** Omitted below 1120px, where it becomes a `<Sheet>`. Spans the full height
   *  of the page wherever it has a column. */
  ticket?: ReactNode
  /**
   * The roll's secondary surface below `lg` (`components.md` §2.8).
   *
   * Where there is a ticket column the resolution sits under the ticket, inside
   * it. Below 1120px there is no column, so it becomes a card here — **above**
   * the tables and never over the chart, because the resolving market must
   * never take space from the tradable one. `lg:hidden` for the same reason the
   * sticky order bar is: the breakpoint that matters is where the ticket loses
   * its column, not where the tables fold away (§F23d).
   */
  /**
   * Pro mode's single region, spanning the orderbook's and the ticket's tracks.
   *
   * When present it **replaces** both — they are not rendered behind it, which
   * matters more than the pixels do: `ticket` is hidden with CSS rather than
   * removed from the DOM, so leaving a live `<OrderTicket>` under the ladder
   * would mount a second set of book, ticker, balance, positions and orders
   * subscriptions (§F23c, one breakpoint over).
   */
  wide?: ReactNode
  aside?: ReactNode
  /** The bottom tab bar and its table. Never under the ticket column, and not
   *  passed below 768px at all: `<TradeRoute>` puts Positions and Orders in the
   *  book's tabs there, and a table hidden here with CSS would still be mounted
   *  and subscribed. The `hidden md:flex` below is the fallback, not the rule. */
  bottom?: ReactNode
  className?: string
}

export function PanelGrid({ chart, book, ticket, wide, aside, bottom, className }: PanelGridProps) {
  return (
    // A column below 768px — chart over book, no tables — and a row of three
    // full-height regions above it.
    // Below 768px it takes its content's height inside a scrolling `<main>`
    // (`<AppShell>`) instead of being squeezed into what is left.
    <div className={cx('flex min-h-0 flex-1 flex-col gap-px bg-bg-canvas max-md:flex-none md:flex-row', className)}>
      {/* The chart's region, and the only one anything stacks under.
          **Below 768px it is not a box at all** (`contents`). The resolving
          card sat inside it, in a region that could not grow, so the card
          squeezed the chart until the canvas drew over the card and the rail —
          it has to stack *after* the chart instead, which is what dissolving
          the region does. The floor moved onto the chart's own cell.
          That cell carries a **floor**, not a share and not its content
          height. A share of a short viewport is less than the chart's own 240px
          minimum plus its header and the rail, and a box shorter than its
          content does not clip — it *overflows*, and the canvas draws over the
          rail's chips. Content height does not work either: every ancestor down
          to the canvas is `flex-1 min-h-0`, which resolves to nothing inside an
          auto-height parent, so the region measures zero and the overflow is
          total. A floor is the one of the three that holds, and the ladder
          takes the remainder because it is the one of the two that scrolls.
          Above 768px they are side by side and the chart is the panel happy at
          any height, so it is the one that gives. */}
      {/* **768–1119px: the same failure one breakpoint up, fixed the same way.**
          The resolving card stacks here between the chart and the tables, and on
          a short window — a laptop with a wallet's side panel open — chart, card
          and tables did not fit: the chart was squeezed below its own minimum
          and drew its axis and the rail over the card. The chart keeps a floor
          and the region scrolls instead, so the card and the tables move below
          it rather than under it. */}
      {/* **From 768px up the region scrolls, and only when it has to** (the
          spec owner's call, 2026-10-26, with the troll box in the tables'
          row). The tables take 40% of the column, never under 240px; the chart
          takes the rest, never under its 360px floor. Where both fit — any
          ordinary desktop window — nothing scrolls. Where they do not, the
          chart keeps its floor and the region scrolls, chart and tables
          together, rather than squeezing either. The header, the strip, the
          book and the ticket never move: the page itself still never scrolls.
          The tape beside the chart (`<TradeRoute>`) takes the same height as
          the tables, so its header lands on their tab row whenever nothing is
          scrolled. The wheel reaches the region over the chart because the
          default `market` timeframe hands the chart no wheel of its own. */}
      <div data-testid="chart-region" className="ud-scroll contents md:flex md:min-h-0 md:min-w-0 md:flex-1 md:flex-col md:gap-px md:overflow-y-auto">
        <div className="flex min-h-[calc(400*var(--ud-u))] min-w-0 shrink-0 flex-col md:min-h-[calc(360*var(--ud-u))] md:flex-1">
          {chart}
        </div>
        {aside ? <div className="shrink-0 bg-bg-surface lg:hidden">{aside}</div> : null}
        {bottom ? (
          // The tables are the first thing to go when there is no room for them;
          // below 768px the single column is strip → chart → sticky buttons.
          <div className="hidden h-[max(calc(240*var(--ud-u)),40%)] shrink-0 flex-col md:flex">{bottom}</div>
        ) : null}
      </div>

      {wide ? (
        /* Pro mode. One region across both tracks, `shrink-0` for the same
           reason the ticket's is: the thing the user is clicking must not
           narrow because the chart wants room.

           **It is the two tracks summed, at both of their widths.** The book is
           260px from `md` and `--ud-orderbook-width` only from `xl`, so a single
           `calc` off the token is 40px too wide for the whole of 1120–1439 —
           most laptops — and the ladder would take those 40px out of the chart
           rather than reporting it. The 260 literal is the same one the book
           track carries below, with the same justification. Summed rather than
           given its own token because a `--ud-pro-ladder-width` is a third
           number somebody has to keep equal to two others. */
        <div
          className={cx(
            'flex min-h-0 shrink-0 flex-col',
            'w-[calc(260*var(--ud-u)+var(--ud-ticket-width)+var(--ud-panel-gap))]',
            'xl:w-[calc(var(--ud-orderbook-width)+var(--ud-ticket-width)+var(--ud-panel-gap))]',
          )}
        >
          {wide}
        </div>
      ) : (
        <>
          {/* The ladder, full height from 768px up. Below that it is a stacked
              row sharing the column's height with the chart, which is what
              `flex-1` buys and `md:flex-none` gives back once it has a width of
              its own. */}
          <div
            className={cx(
              'flex min-h-0 min-w-0 flex-1 flex-col',
              // A phone scrolls to it, so it needs a height of its own:
              // `flex-1` inside a scroller is a share of nothing, and the
              // ladder under it would measure zero.
              'max-md:h-[70svh] max-md:flex-none',
              'md:w-[calc(260*var(--ud-u))] md:flex-none',
              // ≥1440: the orderbook takes its designed width.
              'xl:w-[var(--ud-orderbook-width)]',
            )}
          >
            {book}
          </div>

          {/* `shrink-0` so a wide chart cannot squeeze the form the user is
              filling in. */}
          {ticket ? (
            <div className="hidden min-h-0 w-[var(--ud-ticket-width)] shrink-0 flex-col lg:flex">
              {ticket}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
